/**
 * The rail's first card: the one number the reader opened the link for, and the
 * dates that decide when it matters.
 *
 * It repeats figures the totals block already prints, and that repetition is
 * the point. The totals sit at the foot of a line table that can run for two
 * screens; the rail says what is owed and by when without any scrolling at all.
 *
 * Which number is headlined follows the document, not the layout: an invoice
 * with a part payment against it headlines what is still open, and everything
 * else headlines its total.
 */
import { Card, CardContent } from "@wa/ui";
import { useT, useI18n } from "../../i18n/I18nProvider";
import { fmtDate, fmtMoney } from "../../lib/format";
import type { PublicDoc } from "../../lib/publicDoc";

export function DocSummary({ doc }: { doc: PublicDoc }) {
  const t = useT();
  const { locale } = useI18n();
  const outstanding = doc.type === "invoice" && doc.paidCents > 0;

  const dates: [string, string][] = [];
  if (doc.issueDate) dates.push([t("publicDoc.issueDate"), fmtDate(doc.issueDate, locale)]);
  if (doc.dueDate) dates.push([t("publicDoc.dueDate"), fmtDate(doc.dueDate, locale)]);
  if (doc.validUntil) dates.push([t("publicDoc.validUntil"), fmtDate(doc.validUntil, locale)]);

  return (
    <Card>
      <CardContent className="pt-6">
        <p className="text-xs uppercase tracking-wide text-muted-foreground">{t("publicDoc.summary")}</p>

        <p className="mt-3 text-sm text-muted-foreground">
          {outstanding ? t("publicDoc.due") : t("publicDoc.total")}
        </p>
        <p className="text-3xl font-semibold tracking-tight tabular-nums text-foreground">
          {fmtMoney(outstanding ? doc.dueCents : doc.totalCents, doc.currency, locale)}
        </p>

        {dates.length > 0 ? (
          <dl className="mt-4 space-y-1.5 border-t border-border pt-4 text-sm">
            {dates.map(([label, value]) => (
              <div key={label} className="flex justify-between gap-4">
                <dt className="text-muted-foreground">{label}</dt>
                <dd className="font-medium text-foreground">{value}</dd>
              </div>
            ))}
          </dl>
        ) : null}
      </CardContent>
    </Card>
  );
}
